import { NavContext } from '@ionic/react';
import React, { createContext, useContext, useState } from 'react';

import {
  ClinicGroup,
  fetchBranches,
  fetchClinicGroup
} from '@/data/ClinicGroups';
import { Clinic } from '@/data/Clinics';
import { useToast } from '@/hooks';
import { ROUTES, getScheduleRoute } from '@/utils/routes';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

interface ClinicGroupContextProps {
  branches: Clinic[];
  clinicGroup: ClinicGroup | null;
  loading: boolean;
  selectedBranch: Clinic | null;
  setSelectedBranch: React.Dispatch<React.SetStateAction<Clinic | null>>;
  onSelectBranch: (clinicId: string) => void;
}

const ClinicGroupContext = createContext<ClinicGroupContextProps | undefined>(
  undefined
);

interface ClinicGroupProviderProps {
  children: React.ReactNode;
}

export const ClinicGroupProvider: React.FC<ClinicGroupProviderProps> = ({
  children
}) => {
  const { navigate } = useContext(NavContext);
  const displayToast = useToast();
  const { id: groupCode } = useParams<{ id: string }>();

  const [selectedBranch, setSelectedBranch] = useState<Clinic | null>(null);

  const {
    data: clinicGroup,
    isLoading: loadingClinicGroup
  }: UseQueryResult<ClinicGroup | null, Error> = useQuery({
    queryKey: ['clinicGroup', groupCode],
    queryFn: async () => {
      if (!groupCode) {
        navigate(ROUTES.INVALID);
        return null;
      }

      try {
        const group = await fetchClinicGroup(groupCode);

        if (!group) {
          displayToast({
            message: 'Clinic group not found.',
            duration: 5000,
            position: 'top',
            positionAnchor: 'logo'
          });
          navigate(ROUTES.INVALID);
        }

        return group;
      } catch (error) {
        displayToast({
          message: `Error fetching clinic group: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          duration: 5000,
          position: 'top',
          positionAnchor: 'logo'
        });
        navigate(ROUTES.INVALID);
        return null;
      }
    },
    enabled: !!groupCode
  });

  const {
    data: branches,
    isLoading: loadingBranches
  }: UseQueryResult<Clinic[], Error> = useQuery({
    queryKey: ['branches', groupCode],
    queryFn: async () => {
      if (!groupCode) return [] as Clinic[];

      try {
        return await fetchBranches(groupCode);
      } catch (error) {
        displayToast({
          message: `Error fetching branches: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          duration: 5000,
          position: 'top',
          positionAnchor: 'logo'
        });
        return [] as Clinic[];
      }
    },
    enabled: !!groupCode && !!clinicGroup
  });

  const onSelectBranch = (clinicId: string) => {
    navigate(getScheduleRoute(clinicId));
  };

  const loading = loadingClinicGroup || loadingBranches;

  return (
    <ClinicGroupContext.Provider
      value={{
        branches: branches ?? [],
        clinicGroup: clinicGroup ?? null,
        loading,
        selectedBranch,
        setSelectedBranch,
        onSelectBranch
      }}
    >
      {children}
    </ClinicGroupContext.Provider>
  );
};

export const useClinicGroup = () => {
  const context = useContext(ClinicGroupContext);
  if (context === undefined) {
    throw new Error('useClinicGroup must be used within a ClinicGroupProvider');
  }
  return context;
};
